<template>
  <Popup
    v-if="shouldShowPopup"
    :visible="popupVisible"
    :position="config.position || 'center'"
    :animation="config.animation || 'scale'"
    :mask-closable="config.maskClosable !== false"
    @close="handleClose">
    <div class="dialog-container" :class="{ 'dialog-container--with-icon': config.iconType }">
      <button class="dialog-close" type="button" @click="handleClose">
        <SvgIcon name="svg/close.svg" size="20px" />
      </button>

      <img
        v-if="config.iconType"
        class="dialog-icon"
        :src="config.iconType === 'warning' ? warningGif : successGif"
        alt="" />

      <!-- 标题 -->
      <div v-if="config.title || $slots.title" class="dialog-header">
        <h3 class="dialog-title">
          <slot name="title">{{ config.title }}</slot>
        </h3>
      </div>

      <!-- 内容 -->
      <div class="dialog-content">
        <slot name="content">
          <div v-if="typeof config.content === 'string'" v-html="config.content" />
          <component v-else-if="config.content" :is="config.content" v-bind="config.props || {}" />
        </slot>
      </div>

      <!-- 按钮区域 -->
      <div v-if="config.actions && config.actions.length > 0" class="dialog-actions">
        <button
          v-for="(action, index) in config.actions"
          :key="index"
          :class="[
            'dialog-button',
            `dialog-button--${action.type || 'default'}`,
            { 'dialog-button--last': index === config.actions.length - 1 }
          ]"
          @click="handleAction(action, index)">
          {{ action.text }}
        </button>
      </div>

      <!-- 默认确认/取消按钮 -->
      <div v-else-if="config.showActions !== false" class="dialog-actions">
        <button
          v-if="config.showCancel !== false"
          class="dialog-button dialog-button--plain"
          @click="handleCancel">
          {{ config.cancelText || $t('取消') }}
        </button>
        <button
          :class="[
            'dialog-button dialog-button--primary',
            { 'dialog-button--last': config.showCancel === false }
          ]"
          @click="handleConfirm">
          {{ config.confirmText || $t('确定') }}
        </button>
      </div>
    </div>
  </Popup>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import Popup from '@/components/base/popup/Popup.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import type { DialogConfig, DialogAction } from '@/types/dialog'
import successGif from '@/assets/imgs/dialog/success_160.gif'
import warningGif from '@/assets/imgs/dialog/warning_min.gif'

// Emits 定义 (保留以便与外部保持兼容)
const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

// 响应式状态
const shouldShowPopup = ref(false) // 控制是否渲染Popup组件
const popupVisible = ref(false) // 控制Popup的显示/隐藏动画
const config = reactive<DialogConfig>({})

// 方法
const show = (options: DialogConfig) => {
  if (shouldShowPopup.value) return // 防止重复调用

  // 设置配置并显示弹窗
  Object.assign(config, {
    maskClosable: true,
    showActions: true,
    showCancel: true,
    ...options
  })

  shouldShowPopup.value = true
  popupVisible.value = true
  emit('update:visible', true)
}

const hide = () => {
  // 先触发关闭动画
  popupVisible.value = false
  emit('update:visible', false)

  // 等动画完成后销毁组件
  setTimeout(() => {
    shouldShowPopup.value = false
  }, 300) // 等待动画完成
}

const handleClose = () => {
  config.onClose?.()
  hide()
}

// 统一的按钮处理函数
const handleButtonAction = async (
  handler?: () => void | Promise<void>,
  errorMsg = 'Dialog action error'
) => {
  try {
    await handler?.()
    hide()
  } catch (error) {
    console.error(errorMsg, error)
  }
}

const handleConfirm = () => handleButtonAction(config.onConfirm, 'Dialog confirm error')
const handleCancel = () => handleButtonAction(config.onCancel, 'Dialog cancel error')
const handleAction = (action: DialogAction, index: number) =>
  handleButtonAction(action.handler, `Dialog action ${index} error`)

// 暴露方法
defineExpose({
  show,
  hide
})
</script>

<style lang="scss" scoped>
// 变量定义
$dialog-border-radius: 10px;
$dialog-shadow: none;
$button-height: 40px;
$button-border-radius: 10px;
$transition-duration: 0.2s;

// 颜色变量
$color-text-primary: #fff;
$color-text-secondary: #fff;
$color-bg-white: #141312;
$color-bg-light: #333;
$color-bg-hover: #3a3a3a;
$color-border: #aa925f;
$color-success: linear-gradient(90deg, #d6a443 0%, #c07526 72%, #a15816 100%);

$color-primary: #0969da;
$color-primary-hover: #0860ca;
$color-error: #da3633;
$color-error-hover: #c93c37;

.dialog-container {
  width: 400px;
  min-height: 240px;
  overflow: hidden;
  background: $color-bg-white;
  border-radius: $dialog-border-radius;
  box-shadow: $dialog-shadow;
  align-self: center;
  margin: 0 auto;
  position: relative;
  padding: 56px 50px 40px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;

  &--with-icon {
    height: 420px;
    padding-top: 69px;
  }
}

.dialog-close {
  position: absolute;
  top: 17px;
  right: 16px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #e8ba6e;
  cursor: pointer;
}

.dialog-icon {
  width: 120px;
  height: 120px;
  object-fit: contain;
  align-self: center;
  margin-bottom: 10px;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.dialog-title {
  margin: 0;
  font-size: 18px;
  line-height: normal;
  font-weight: 700;
  color: $color-text-primary;
  text-align: center;
}

.dialog-content {
  width: 300px;
  min-height: 34px;
  margin: 0 auto;
  padding: 0;
  font-size: 14px;
  color: $color-text-primary;
  text-align: center;
  line-height: normal;
}

.dialog-actions {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  padding: 0;
}

.dialog-button {
  flex: 0 1 250px;
  height: $button-height;
  border: none;
  border-radius: $button-border-radius;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all $transition-duration ease;
  outline: none;

  &:active {
    transform: translateY(1px);
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.3);
  }

  // 按钮类型样式
  &--default {
    background: $color-success;
    color: #fff;
    border: none;
    box-shadow: 0 5px 10px 0 #382206;

    &:hover {
      filter: brightness(1.05);
    }
  }

  &--primary {
    background: $color-success;
    color: white;
    border: none;
    box-shadow: 0 5px 10px 0 #382206;

    &:hover {
      filter: brightness(1.05);
    }
  }

  &--outline {
    background: transparent;
    color: $color-primary;
    border: 1px solid $color-primary;

    &:hover {
      background: #f0f6ff;
    }
  }

  &--plain {
    background: linear-gradient(90deg, #5a6570 0%, #3b3f49 100%);
    color: $color-text-secondary;
    border: none;
    box-shadow: 0 5px 10px 0 #272727;
    &:hover {
      color: $color-text-primary;
      filter: brightness(1.05);
    }
  }
}
</style>
